import { useApolloClient } from "@apollo/client";
import * as R from "requests";
import {
  saveAuthorizations,
  saveBreeds,
  saveDisclaimerProcedures,
  saveDisclaimerText,
  saveMedications,
  saveProcedures,
  saveProtocols,
  saveUsers
} from "../utils/db-queries";

const useInitializeOfflineData = () => {
  const client = useApolloClient();

  const loadProcedures = () => {
    client
      .query<R.GetAllProceduresQuery, R.GetAllProceduresQueryVariables>({
        query: R.GetAllProceduresDocument,
        fetchPolicy: "no-cache",
        variables: {}
      })
      .then(({ data }) => {
        const procedures = data.procedures?.collection || [];
        return saveProcedures(procedures);
      });
  };

  const loadProtocols = () => {
    client
      .query<R.GetAllProtocolsQuery, R.GetAllProtocolsQueryVariables>({
        query: R.GetAllProtocolsDocument,
        fetchPolicy: "no-cache",
        variables: {}
      })
      .then(({ data }) => {
        const protocols = data.anestheticProtocols?.collection || [];
        return saveProtocols(protocols);
      });
  };

  const loadUsers = () => {
    client
      .query<R.GetAllUsersQuery, R.GetAllUsersQueryVariables>({
        query: R.GetAllUsersDocument,
        fetchPolicy: "no-cache",
        variables: {}
      })
      .then(({ data }) => {
        const users = data.users?.collection || [];
        return saveUsers(users);
      });
  };

  const loadBreeds = () => {
    client
      .query<R.GetAllBreedsQuery, R.GetAllBreedsQueryVariables>({
        query: R.GetAllBreedsDocument,
        fetchPolicy: "no-cache",
        variables: {}
      })
      .then(({ data }) => {
        const breeds = data.breeds?.collection || [];
        return saveBreeds(breeds);
      });
  };

  const loadMedications = () => {
    client
      .query<R.GetAllMedicationsQuery, R.GetAllMedicationsQueryVariables>({
        query: R.GetAllMedicationsDocument,
        fetchPolicy: "no-cache",
        variables: {}
      })
      .then(({ data }) => {
        const medications = (data.medications?.collection || []).map(item => ({
          ...item,
          concentration: item.concentration || null
        }));
        saveMedications(medications);
      });
  };

  const loadDisclaimers = (organizationId: number) => {
    client
      .query<R.GetAllDisclaimersQuery, R.GetAllDisclaimersQueryVariables>({
        query: R.GetAllDisclaimersDocument,
        fetchPolicy: "no-cache",
        variables: { organizationId }
      })
      .then(({ data }) => {
        const disclaimerProcedures =
          data.organization?.organizationSetting?.disclaimerProcedureSettings
            ?.collection || [];
        const authorizations =
          data.organization?.organizationSetting?.authorizationSettings
            ?.collection || [];
        const disclaimerText =
          data.organization?.organizationSetting?.disclaimerText || "";
        saveDisclaimerProcedures(disclaimerProcedures);
        saveAuthorizations(authorizations);
        saveDisclaimerText(disclaimerText);
      });
  };

  return {
    loadProtocols,
    loadBreeds,
    loadUsers,
    loadDisclaimers,
    loadProcedures,
    loadMedications,
    initOfflineData: (organizationId?: number) => {
      loadProcedures();
      loadUsers();
      loadBreeds();
      loadMedications();
      loadProtocols();
      if (organizationId) {
        loadDisclaimers(organizationId);
      }
    }
  };
};

export default useInitializeOfflineData;
